import { Button, Stack, Table, Typography } from '@mui/joy';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import PlusCircleIcon from '@/assets/icons/plus-circle.svg?react';
import TrashIcon from '@/assets/icons/trash.svg?react';
import { OrganizationStakeholderForm } from '@/components/organizations/OrganizationStakeholderForm';
import { ActionMenu } from '@/components/processes/ActionMenu';
import { useAppAbility } from '@/hooks/useAppAbility';
import { I18N_DE } from '@/i18n/de';
import { Action, Subject } from '@/modules/api';
import { backendApiOrganizationsRemoveOrganizationStakeholderMutation } from '@/modules/api/@tanstack/react-query.gen';
import { useMyOrganizationQuery } from '@/queries/useMyOrganizationQuery';
import { theme } from '@/setup/theme';

function RouteComponent() {
  const { data: organization } = useMyOrganizationQuery({
    adminLevels: true,
    users: true,
    stakeholders: true,
  });
  const { ability } = useAppAbility();
  const [showForm, setShowForm] = useState(false);

  const { mutate: removeMutate, isPending } = useMutation(
    backendApiOrganizationsRemoveOrganizationStakeholderMutation(),
  );

  return (
    organization && (
      <Stack gap={2}>
        <Typography level="h2">Stakeholder</Typography>

        <Table>
          <thead>
            <tr>
              <th style={{ width: '40%' }}>Organisation</th>
              <th style={{ width: '30%' }}>Bearbeiter*in</th>
              <th style={{ width: '20%' }}>Art der Eingabe</th>
              <th style={{ width: '10%' }}>Aktion</th>
            </tr>
          </thead>
          <tbody>
            {organization.stakeholders?.map((stakeholder) => (
              <tr key={stakeholder.id}>
                <td>{stakeholder.stakeholderOrganization?.name || stakeholder.contactOrganization}</td>
                <td>
                  {stakeholder.stakeholderUser
                    ? `${stakeholder.stakeholderUser.firstName} ${stakeholder.stakeholderUser.lastName}`
                    : stakeholder.contactUser}
                </td>
                <td>{I18N_DE.ExaminationType[stakeholder.type]}</td>
                <td aria-label="MenuButton">
                  <ActionMenu
                    actions={[
                      {
                        label: 'Löschen',
                        icon: <TrashIcon />,
                        action: async () => {
                          if (isPending) return;

                          removeMutate({ path: { organizationId: organization.id, stakeholderId: stakeholder.id } });
                        },
                      },
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {ability.can(Action.CREATE, Subject.OrganizationStakeholder)}
        <Stack direction="row">
          <Button
            size="md"
            variant="plain"
            onClick={() => setShowForm(true)}
            startDecorator={<PlusCircleIcon stroke={theme.palette.primary.plainColor} />}
            disabled={showForm}
          >
            Stakeholder hinzufügen
          </Button>
        </Stack>

        {showForm && (
          <OrganizationStakeholderForm organizationId={organization.id} onAbort={() => setShowForm(false)} />
        )}
      </Stack>
    )
  );
}

export const Route = createFileRoute('/_authenticated/_management/meine-organisation/stakeholder/')({
  component: RouteComponent,
});
